import type { ImageScenario } from './imageTypes';
import type { Partner, Tool } from './partners';

/**
 * Admin API client.
 *
 * Every call here is also gated server-side. `isAdminUi` decides whether to
 * SHOW the menu; it decides nothing about access. Treat it as presentation
 * only — the server checks the email inside the signed session token on every
 * one of these routes, and a non-admin calling them directly gets a 404.
 */

const TOKEN_KEY = 'devtool-detective.session';

function token(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

async function call<T>(
  path: string,
  init: RequestInit = {},
): Promise<{ ok: boolean; status: number; body: T | null }> {
  const bearer = token();
  try {
    const response = await fetch(path, {
      ...init,
      headers: {
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
        ...(init.headers ?? {}),
      },
    });
    let body: T | null = null;
    try {
      body = (await response.json()) as T;
    } catch {
      body = null;
    }
    return { ok: response.ok, status: response.status, body };
  } catch {
    return { ok: false, status: 0, body: null };
  }
}

/**
 * Is this session an admin?
 *
 * Asked of the server rather than inferred from the email, because the
 * allowlist lives in the server's environment and the client has no business
 * knowing its contents. A 404 means "not an admin" and is indistinguishable
 * from "no such route", which is the point.
 */
export async function checkAdmin(): Promise<boolean> {
  const result = await call<{ ok: boolean }>('/api/admin/whoami');
  return result.ok && Boolean(result.body?.ok);
}

/** Whether this server can draft answers at all. */
export async function draftingAvailable(): Promise<boolean> {
  const result = await call<{ ok: boolean; drafting?: boolean }>('/api/admin/whoami');
  return Boolean(result.body?.drafting);
}

export interface AdminContent {
  levels: ImageScenario[];
  overrides: Record<string, Partial<ImageScenario>>;
  order: string[];
}

export async function fetchAdminContent(): Promise<AdminContent | null> {
  const result = await call<{ ok: boolean } & AdminContent>('/api/admin/content');
  if (!result.ok || !result.body) return null;
  return {
    levels: result.body.levels ?? [],
    overrides: result.body.overrides ?? {},
    order: result.body.order ?? [],
  };
}

export interface SaveOutcome {
  ok: boolean;
  problems: string[];
  message: string;
}

/** Upload a screenshot. Returns the URL to store on the level. */
export async function uploadScreenshot(
  file: Blob,
  levelId: string,
): Promise<{ ok: boolean; src?: string; message: string }> {
  const bearer = token();
  try {
    const response = await fetch('/api/admin/upload', {
      method: 'POST',
      headers: {
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
        'Content-Type': 'application/octet-stream',
        'X-Level-Id': levelId,
      },
      body: file,
    });
    const body = (await response.json().catch(() => null)) as
      | { ok?: boolean; src?: string; error?: string }
      | null;
    if (!response.ok || !body?.src) {
      return {
        ok: false,
        message: body?.error ?? `Upload failed (${response.status}).`,
      };
    }
    return { ok: true, src: body.src, message: 'Screenshot stored.' };
  } catch {
    return { ok: false, message: 'Could not reach the server.' };
  }
}

export async function saveLevel(level: ImageScenario): Promise<SaveOutcome> {
  const result = await call<{ ok: boolean; problems?: string[]; error?: string }>(
    `/api/admin/levels/${encodeURIComponent(level.id)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(level),
    },
  );
  if (result.ok) return { ok: true, problems: [], message: 'Saved. This is live to players now.' };
  return {
    ok: false,
    problems: result.body?.problems ?? [],
    message: result.body?.error ?? `Could not save (${result.status}).`,
  };
}

export async function saveOverride(
  levelId: string,
  patch: Partial<ImageScenario>,
): Promise<SaveOutcome> {
  const result = await call<{ ok: boolean; error?: string }>(
    `/api/admin/overrides/${encodeURIComponent(levelId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  );
  return {
    ok: result.ok,
    problems: [],
    message: result.ok
      ? 'Saved. This is live to players now.'
      : (result.body?.error ?? `Could not save (${result.status}).`),
  };
}

/**
 * Remove a level from the game.
 *
 * A compiled level is hidden rather than deleted — it ships in the bundle — and
 * can be restored. A runtime level is gone for good.
 */
export async function deleteLevel(levelId: string): Promise<SaveOutcome> {
  const result = await call<{ ok: boolean; hidden?: boolean; error?: string }>(
    `/api/admin/levels/${encodeURIComponent(levelId)}`,
    { method: 'DELETE' },
  );
  return {
    ok: result.ok,
    problems: [],
    message: result.ok
      ? result.body?.hidden
        ? 'Removed from the game. It ships in the build, so it can be restored.'
        : 'Deleted.'
      : (result.body?.error ?? 'Could not remove it.'),
  };
}

export async function restoreLevel(levelId: string): Promise<SaveOutcome> {
  const result = await call<{ ok: boolean; error?: string }>(
    `/api/admin/levels/${encodeURIComponent(levelId)}/restore`,
    { method: 'POST' },
  );
  return {
    ok: result.ok,
    problems: [],
    message: result.ok ? 'Restored.' : (result.body?.error ?? 'Could not restore it.'),
  };
}

/** Revert an override, or delete an admin-authored level. */
export async function removeContent(levelId: string): Promise<SaveOutcome> {
  const result = await call<{ ok: boolean; error?: string }>(
    `/api/admin/content/${encodeURIComponent(levelId)}`,
    { method: 'DELETE' },
  );
  return {
    ok: result.ok,
    problems: [],
    message: result.ok ? 'Reverted.' : (result.body?.error ?? 'Could not revert.'),
  };
}

export interface DraftedArrow {
  label: string;
  pointsAt: string;
  real: boolean;
  title: string;
  severity: 'critical' | 'major' | 'minor';
  explanation: string;
  fix: string;
  decoyNote: string;
  confidence: 'high' | 'medium' | 'low';
  missing: boolean;
}

/**
 * Ask the server to draft answers for an uploaded screenshot.
 *
 * The result is a suggestion, not an answer. Measured against the levels whose
 * keys were confirmed by hand, the model was right about what every arrow
 * pointed at and agreed with the author's verdict roughly half the time.
 */
export async function draftAnswers(
  imageSrc: string,
  arrows: { label: string; region: { x: number; y: number; w: number; h: number } }[],
  toolName: string,
): Promise<{ ok: boolean; arrows?: DraftedArrow[]; model?: string; error?: string }> {
  const result = await call<{ ok: boolean; arrows?: DraftedArrow[]; model?: string; error?: string }>(
    '/api/admin/draft',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageSrc, arrows, toolName }),
    },
  );
  if (!result.ok || !result.body?.ok) {
    return { ok: false, error: result.body?.error ?? 'Could not draft the answers.' };
  }
  return { ok: true, arrows: result.body.arrows ?? [], model: result.body.model };
}

export async function savePartner(partner: Partner): Promise<SaveOutcome> {
  const result = await call<{ ok: boolean; problems?: string[]; error?: string }>(
    `/api/admin/partners/${encodeURIComponent(partner.id)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partner),
    },
  );
  return {
    ok: result.ok,
    problems: result.body?.problems ?? [],
    message: result.ok ? 'Vendor saved.' : (result.body?.error ?? 'Could not save the vendor.'),
  };
}

export async function saveTool(tool: Tool): Promise<SaveOutcome> {
  const result = await call<{ ok: boolean; problems?: string[]; error?: string }>(
    `/api/admin/tools/${encodeURIComponent(tool.id)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(tool),
    },
  );
  return {
    ok: result.ok,
    problems: result.body?.problems ?? [],
    message: result.ok ? 'Saved. This is live now.' : (result.body?.error ?? 'Could not save.'),
  };
}

/** Refused while any level still names the tool — the server checks. */
export async function removeTool(toolId: string): Promise<SaveOutcome> {
  const result = await call<{ ok: boolean; error?: string }>(
    `/api/admin/tools/${encodeURIComponent(toolId)}`,
    { method: 'DELETE' },
  );
  return {
    ok: result.ok,
    problems: [],
    message: result.ok ? 'Tool removed.' : (result.body?.error ?? 'Could not remove it.'),
  };
}

/** Persist the display order. The body is the full list, not a delta. */
export async function saveOrder(order: string[]): Promise<SaveOutcome> {
  const result = await call<{ ok: boolean; error?: string }>('/api/admin/order', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ order }),
  });
  return {
    ok: result.ok,
    problems: [],
    message: result.ok ? 'Order saved.' : (result.body?.error ?? 'Could not save the order.'),
  };
}

/**
 * Download an export.
 *
 * Fetched with the bearer token rather than linked, because a plain <a href>
 * cannot carry the Authorization header and the route is admin-only. The blob
 * is then handed to a temporary link so the browser saves it.
 */
export async function downloadUsers(format: 'csv' | 'xlsx'): Promise<SaveOutcome> {
  const bearer = token();
  if (!bearer) return { ok: false, problems: [], message: 'Sign in first.' };
  try {
    const response = await fetch(`/api/admin/users.${format}`, {
      headers: { Authorization: `Bearer ${bearer}` },
    });
    if (!response.ok) {
      return { ok: false, problems: [], message: `Export failed (${response.status}).` };
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `devtool-detective-players.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoked on the next tick: revoking immediately can cancel the download in
    // some browsers before it has read the blob.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return { ok: true, problems: [], message: `Downloaded the ${format.toUpperCase()}.` };
  } catch {
    return { ok: false, problems: [], message: 'Could not reach the server.' };
  }
}

export async function sendSlackTest(): Promise<SaveOutcome> {
  const result = await call<{ ok: boolean; channel?: string; error?: string }>(
    '/api/admin/slack-test',
    { method: 'POST' },
  );
  return {
    ok: result.ok,
    problems: [],
    message: result.ok
      ? `Posted to ${result.body?.channel ?? 'Slack'}.`
      : (result.body?.error ?? 'Could not post to Slack.'),
  };
}

export async function sendSignupEmailTest(): Promise<SaveOutcome> {
  const result = await call<{ ok: boolean; to?: string; error?: string }>(
    '/api/admin/signup-email-test',
    { method: 'POST' },
  );
  return {
    ok: result.ok,
    problems: [],
    message: result.ok
      ? `Sent a sample new-player notice to ${result.body?.to ?? 'the team mailbox'}.`
      : (result.body?.error ?? 'Could not send the notice.'),
  };
}

export interface AuditEntry {
  at: string;
  actor: string;
  action: string;
  target: string;
}

export async function fetchAudit(): Promise<AuditEntry[]> {
  const result = await call<{ ok: boolean; entries: AuditEntry[] }>('/api/admin/audit');
  return result.body?.entries ?? [];
}
